import { useState } from 'react'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { myLanes } from '../lib/support'
import { supabase } from '../lib/supabase'
import { initialsOf } from '../lib/initials'
import { useHubLang } from '../lib/i18n'
import { NOTICES_PAGE } from '../lib/hubCopy'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { Notices } from '../components/Notices'
import { NoticeManager } from '../components/NoticeManager'
import { GuideLink } from '../components/guides/GuideLink'

/**
 * #/admin/notices — SHL-33 (David, 10 Oct: "they don't need to be in a view
 * on hub").
 *
 * ── THIS REVERSES 039's CURATOR-MODE ARGUMENT, DELIBERATELY ─────────────────
 *
 * 039 put the NoticeManager on the hub, under the strip it edits, so a comms
 * admin wrote a notice while looking at what everybody sees; 12 Sep moved its
 * switch into the identity menu as "Manage notices". David ruled on 10 Oct
 * that authoring does not belong in a view of the hub, and moved it here.
 *
 * The BENEFIT 039 argued for is kept, not dropped: the top of this page is the
 * live strip — the same <Notices> component the hub renders, reading the same
 * active_notices() — re-read after every change the manager makes. So "write
 * it while looking at it" is still true; it just is not true ON the hub. Do
 * not move the manager back to Home on the strength of 039's comments; they
 * have been rewritten to point here.
 *
 * The preview has no dismiss buttons: putting a notice away here would put it
 * away for the admin on the real hub too, which is not what a preview is for.
 * The welcome modal is not previewed here; "Show the welcome again" in the
 * identity menu opens the real one.
 */
export function AdminNotices({ me }: { me: Me }) {
  const t = NOTICES_PAGE[useHubLang()]
  const canPublish = can(me, 'content.publish')
  // Bumped by the manager after a write, so the preview re-reads.
  const [version, setVersion] = useState(0)

  return (
    <AppShell
      current="Notices"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={canPublish}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {!canPublish ? (
        <Card title="Notices">
          <p className="empty">{t.noAccess}</p>
        </Card>
      ) : (
        <div className="noticespage">
          <p className="noticespage__lede">{t.lede}</p>
          <Card title={t.previewHeading}>
            <p className="noticespage__note">{t.previewNote}</p>
            <Notices key={version} notices={[]} me={me} preview emptyText={t.previewEmpty} />
          </Card>
          <Card title={t.manageHeading}>
            {/* 353 (HLP-50): writing one (New notice is the manager's first
                control) and what happens after (the list below it). */}
            <p className="noticespage__guides">
              <GuideLink guide="comms-admin" section="notices" label="How writing a notice works" />
              <GuideLink guide="comms-admin" section="lifecycle" label="What happens after a notice is posted" />
            </p>
            <NoticeManager me={me} onChanged={() => setVersion((v) => v + 1)} />
          </Card>
        </div>
      )}
    </AppShell>
  )
}
